import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { type RootState } from '../../../shared/types';
import { useGetEntitlementDetailsQuery } from '../data-layer/detailsApi';
import { detailsActions } from '../data-store/detailsSlice';

/**
 * Entitlement Details Custom Hook
 * Manages fetching, caching, and state management for entitlement details data
 *
 * @hook
 * @description Orchestrates the retrieval of entitlement details based on selected entitlement,
 * handles loading states, error management, and Redux store integration. Provides a unified
 * interface for components to access entitlement data with proper loading indicators.
 *
 * @returns {Object} Hook return object
 * @returns {EntitlementDetailsRowType|undefined} entitlementDetails - Fetched entitlement data
 * @returns {unknown} error - API error if request fails
 * @returns {boolean} isLoading - RTK Query loading state
 * @returns {boolean} showCacheLoader - Additional loading state for UX
 * @returns {number|undefined} entitlementValueKey - Current entitlement identifier
 *
 */

const useEntitlementDetails = () => {
  const dispatch = useDispatch();

  const selectedEntitlementKey = useSelector(
    (state: RootState) =>
      state.aag.details.entitlementSummary.selectedEntitlement
        ?.entitlementValueKey
  );
  const queryParamKey = useSelector(
    (state: RootState) => state.aag.details.queryParam
  );

  const entitlementValueKey = selectedEntitlementKey ?? queryParamKey;

  const {
    data: entitlementDetails,
    error,
    isLoading,
    refetch,
  } = useGetEntitlementDetailsQuery(
    { entitlementValueKey: Number(entitlementValueKey) },
    { skip: !entitlementValueKey }
  );

  useEffect(() => {
    if (entitlementDetails) {
      dispatch(detailsActions.setEntitlementDetailsData(entitlementDetails));
    }

    if (error) {
      dispatch(
        detailsActions.setEntitlementDetailsError(
          'Failed to fetch entitlement details'
        )
      );
    }
  }, [entitlementValueKey, entitlementDetails, error, dispatch]);

  return {
    entitlementDetails,
    error,
    isError: !!error,
    isLoading,
    entitlementValueKey,
    refetch,
  };
};

// eslint-disable-next-line import/prefer-default-export
export { useEntitlementDetails };
